import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SessionContext } from 'auth/SessionProvider';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { getConfigurations } from 'services/configuration';
import {
  getAllToggleFeatures,
  getToggleFeatureAssignees,
} from 'services/toggleFeature';
import { parseIpuUnitCost, resolveCostPerIpuUsd } from 'utils/ipuCost';

export const IPU_COST_FEATURE_KEY = 'ipuCost';

function hasIpuCostFeature(
  features?: Array<{ key?: string }> | null,
) {
  return !!features?.some(feature => feature.key === IPU_COST_FEATURE_KEY);
}

export function useIpuCost(
  region?: string | null,
  ownerUserId?: string | null,
) {
  const { user } = useContext(SessionContext);

  const { data: toggleFeatures } = useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.TOGGLE_FEATURES, 'ipu-cost'],
    queryFn: async () => {
      const res = await getAllToggleFeatures();
      return res.data?.toggleFeatures ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: assignedUserIds } = useQuery({
    queryKey: [
      QueryServerKeys.SYSTEM_MANAGEMENT.TOGGLE_FEATURES,
      'ipu-cost-assignees',
    ],
    queryFn: async () => {
      const res = await getToggleFeatureAssignees(IPU_COST_FEATURE_KEY);
      return ((res.data?.userIds ?? []) as string[]).map(id =>
        String(id).toLowerCase(),
      );
    },
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  });

  const { data: configResult } = useQuery({
    queryKey: [QueryServerKeys.SYSTEM_MANAGEMENT.CONFIGURATIONS, 'ipuUnitCost'],
    queryFn: () => getConfigurations({ page: 1, search: 'ipuUnitCost' }),
    staleTime: 5 * 60 * 1000,
  });

  const feature = toggleFeatures?.find(
    (item: { key?: string }) => item.key === IPU_COST_FEATURE_KEY,
  );
  const isPublished = !!feature?.published;
  const sessionUserId = user?.uuid || user?.id;
  const isAssignedToMe =
    hasIpuCostFeature(user?.features) ||
    !!(
      sessionUserId &&
      assignedUserIds?.includes(String(sessionUserId).toLowerCase())
    );
  const isAssignedToOwner = !!(
    ownerUserId && assignedUserIds?.includes(ownerUserId.toLowerCase())
  );
  const showIpuCost = isPublished || isAssignedToMe || isAssignedToOwner;

  const rawValue = configResult?.data?.configurations?.find(
    (item: { configKey?: string }) => item.configKey === 'ipuUnitCost',
  )?.configValue;
  const costConfig = parseIpuUnitCost(rawValue);
  const costPerIpuUsd = resolveCostPerIpuUsd(costConfig, region);

  return { showIpuCost, costPerIpuUsd, costConfig };
}
